<template>
  <transition name="fade">
    <div :class="['asMenu', `${isMinified ? '-mini' : ''}`, classList]">
      <div v-if="minify"
        class="asMenu_arrow"
        :class="{ '-rotate': isMinified }"
        @click="handlerMinify"
        data-analytics
        data-analytics-element-type="other"
        data-analytics-element-id="toolbar_btn"
      />
      <template v-for="(item, i) in menu">
        <template v-if="item">
          <div
            v-if="!item.type || item.type === 'router-link'"
            class="asMenu_item"
            :key="i"
            :class="[
              `-${i}`,
              isMinified ? '-mini' : ''
            ]"
            data-analytics
            data-analytics-element-type="menu_sidebar"
            :data-analytics-element-target="typeof item.link === 'string' ? item.link : item.link.name"
            :data-analytics-element-id="i"
            :data-analytics-element-name="item.name"
            :data-cy="`menu_sidebar_${i}`"
            @click="goTo(i, item)"
          >
            <i class="asMenu_icon" :class="item.icon"></i>
            <span class="asMenu_text">
              <span class="asMenu_name">{{item.name}}</span>
              <span v-if="item.note" class="asMenu_note">{{item.note}}</span>
            </span>
          </div>

          <a
            v-if="item.type === 'link'"
            class="asMenu_item"
            :href="item.link"
            :target="item.target"
            :key="i"
            :class="[
              `-${i}`,
              isMinified ? '-mini' : ''
            ]"
            data-analytics
            data-analytics-element-type="menu_sidebar"
            :data-analytics-element-target="typeof item.link === 'string' ? item.link : item.link.name"
            :data-analytics-element-id="i"
            :data-analytics-element-name="item.name"
            :data-cy="`menu_sidebar_${i}`"
          >
            <i class="asMenu_icon" :class="item.icon"></i>
            <span class="asMenu_text">
              <span class="asMenu_name">{{item.name}}</span>
              <span v-if="item.note" class="asMenu_note">{{item.note}}</span>
            </span>
          </a>

          <span
            v-if="item.type === 'action'"
            class="asMenu_item"
            @click="item.action"
            :key="i"
            :class="[
              `-${i}`,
              isMinified ? '-mini' : ''
            ]"
            data-analytics
            data-analytics-element-type="menu_sidebar"
            :data-analytics-element-target="typeof item.link === 'string' ? item.link : item.link.name"
            :data-analytics-element-id="i"
            :data-analytics-element-name="item.name"
            :data-cy="`menu_sidebar_${i}`"
          >
            <i class="asMenu_icon" :class="item.icon"></i>
            <span class="asMenu_text">
              <span class="asMenu_name">{{item.name}}</span>
              <span v-if="item.note" class="asMenu_note">{{item.note}}</span>
            </span>
          </span>

          <div
            :key="i"
            v-if="item.type === 'modal'"
            class="asMenu_item"
            :class="[
              `-${i}`,
              isMinified ? '-mini' : ''
            ]"
          >
            <i class="asMenu_icon" :class="`${item.icon}`"></i>
            <as-external-document
              :doc="{
                url: item.link,
                title: item.name
              }"
              :is-modal="true"
              :icon="false"
              class="asMenu_text"
            />
          </div>

          <div
            v-if="item.type === 'club'"
            :key="i"
            class="asMenu_item"
            :class="[
              `-${i}`,
              isMinified ? '-mini' : ''
            ]"
            @click="handlerClubLookAll(true)"
          >
            <i class="asMenu_icon" :class="item.icon"></i>
            <span class="asMenu_text">
              <span class="asMenu_name">{{item.name}}</span>
            </span>
          </div>
        </template>
      </template>

      <as-menu-contacts v-if="type !== 'sidebar'" />
    </div>
  </transition>

  <modal-choose-club
    v-model="showModalChooseClub"
    :communities="communitiesModalChooseClub || []"
    @chooseClub="handlerChooseClub($event)"
  />
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'

import ModalChooseClub from '@web/components.v2/Home/Club/ModalChooseClub.vue'
import AsExternalDocument from '@web/components/AsExternalDocument.vue'
import AsMenuContacts from '@web/components/AsMenuContacts.vue'

import { Menu } from '@web/store/types/modules/menu'
import Analytics from '@web/services/Analytics/Analytics'
import { useRouter } from 'vue-router'
import useClub from '@web/composition/useClub'

export default defineComponent({
  name: 'AsMenu',
  model: {
    prop: 'mini',
    event: 'minify'
  },
  components: {
    AsExternalDocument,
    ModalChooseClub,
    AsMenuContacts
  },
  props: {
    classList: {
      type: String,
      default: ''
    },
    menu: {
      type: Object as () => Menu,
      default: () => ({} as Menu)
    },
    status: {
      type: String,
      default: '-open'
    },
    minify: {
      type: Boolean,
      default: false
    },
    mini: {
      type: Boolean,
      default: false
    },
    type: {
      type: String as () => 'menu' | 'sidebar',
      default: 'menu'
    }
  },
  emits: ['minify', 'click:item'],
  setup (props, ctx) {
    const router = useRouter()
    const isMinified = ref(props.mini)

    const {
      handlerClubLookAll,
      showModalChooseClub,
      communitiesModalChooseClub,
      handlerChooseClub
    } = useClub()

    function handlerMinify () {
      const _isMinified = !isMinified.value
      isMinified.value = _isMinified
      ctx.emit('minify', _isMinified)
    }

    function goTo (i, item) {
      ctx.emit('click:item', item)
      if (i === 'support') {
        Analytics.send({
          category: 'support_activity',
          action: 'open_support',
          label: props.type
        })
      }

      router.push(item.link)
    }

    return {
      isMinified,
      handlerMinify,
      goTo,
      handlerClubLookAll,
      showModalChooseClub,
      communitiesModalChooseClub,
      handlerChooseClub
    }
  }
})
</script>

<style lang="scss">
  @import '@web/assets/scss/vars.scss';

  .asMenu {
    position: relative;
    width: 100%;
    background: var(--ac-color-white);
    font-family: Inter, sans-serif;
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 100%;

    @media (min-width: $bp-desktop) {
      background-color: #f6f6f6;
      border-radius: 10px;
      width: 352px;
      height: auto;

      &.-mini {
        width: 86px;
      }
    }

    &.-course {
      width: 100%;
      margin-top: 20px;
      border-radius: 0;
    }

    &_arrow {
      position: absolute;
      top: 24px;
      left: -28px;

      display: none;
      width: 14px;
      height: 25px;

      background-image: url(~@web/assets/images/icon-menu-arrow.svg);
      background-repeat: no-repeat;
      background-size: cover;
      transition: all .3s;
      cursor: pointer;

      &.-rotate {
        transform: rotate(180deg);
      }

      @media (min-width: $bp-desktop) {
        display: block;
      }
    }

    &_item {
      position: relative;

      display: flex;
      align-items: center;
      height: 60px;
      padding: 0 35px 0 72px;

      color: #1E1F22;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      text-decoration: none;

      background: var(--ac-color-white);
      border-bottom: 1px solid #E4E4E4;
      transition: color .3s;
      cursor: pointer;

      &:hover,
      &:active,
      &:focus {
        color: rgb(0, 0, 0);
      }

      &:last-child {
        border: none !important;
      }

      @media (min-width: $bp-desktop) {
        padding-left: 55px;
        padding-right: 25px;
        border-radius: 10px;
        background: white;
        color: rgb(76, 82, 100);
        border-bottom: 1px solid rgb(226, 232, 237);

        &.-mini {
          padding: 0;
        }

        &.-mini:hover {
          position: relative;
          z-index: 10;

          padding-left: 55px;
          min-width: 352px;

          overflow: hidden;
          border-color: transparent;
          box-shadow: 0 0 25px rgba(13, 22, 80, 0.3);
          border-radius: 0 10px 10px 0;

          &:first-of-type {
            border-radius: 10px 10px 10px 0;
          }

          &:last-of-type {
            border-radius: 0 10px 10px 10px;
          }
        }
      }

      &.-exit {
        border-bottom: none;
      }

      &.-current {
        @media (min-width: $bp-tablet) {
          position: relative;
          background-color: #f6f6f6;

          &::after {
            content: '';

            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;

            width: 11px;
            background-image: linear-gradient(9deg, #3e62f9 0%, #b03ffb 100%);
          }
        }

        @media (min-width: $bp-desktop) {
          background-color: #f6f6f6;

          &::after {
            display: none;
          }
        }
      }
    }

    &_name {
      white-space: pre;

      &:first-letter {
        text-transform: uppercase;
      }
    }

    @media (min-width: $bp-desktop) {
      &.-mini &_name {
        display: none;
      }

      &_item:hover &_name {
        display: inline;
      }
    }

    &.-inner &_item {
      height: 57px;
      font-size: 14px;
      border-top: 1px solid rgb(226, 232, 237);
      border-bottom: 0;
    }

    &.-borderless &_item {
      border: 0;
    }

    &.-course &_item {
      border-radius: 0;

      &:first-child {
        border: 0;
      }
    }

    &.-setting &_item {
      height: 63px;
    }

    &_icon {
      position: absolute;
      top: 50%;
      left: 38px;

      transform: translateY(-50%);
      background-repeat: no-repeat;

      @media (min-width: $bp-desktop) {
        left: 20px;
      }

      &.-course {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-mycourses.svg);
      }

      &.-chat {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-messages.svg);
      }

      &.-homework {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-homeworks.svg);
      }

      &.-userEdit {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-profile-edit.svg);
      }

      &.-stat {
        width: 21px;
        height: 21px;

        background-image: url(~@web/assets/images/icon-stat.svg);
      }

      &.-subscribe {
        width: 23px;
        height: 21px;

        background-image: url(~@web/assets/images/icon-subscribe.svg);
      }

      &.-referral {
        width: 25px;
        height: 25px;

        background-image: url(~@web/assets/images/icon-referral.svg);
      }

      &.-instruction {
        width: 20px;
        height: 20px;
        background-size: contain;
        background-position: center;
        background-image: url(~@web/assets/images/icon-instruction.svg);
      }

      &.-support {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-support.svg);
      }

      &.-subscriptions {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-subscriptions.svg);
      }

      &.-videoconsultations {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-videoconsultations.svg);
        background-size: contain;
      }

      &.-password {
        width: 18px;
        height: 21px;

        background-image: url(~@web/assets/images/icon-password.svg);
      }

      &.-payment {
        width: 20px;
        height: 15px;

        background-image: url(~@web/assets/images/icon-payment.svg);
      }

      &.-exit {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-logout.svg);
      }

      &.-ext {
        width: 20px;
        height: 20px;

        background-image: url(~@web/assets/images/icon-ext.svg);
      }

      &.-settings {
        width: 24px;
        height: 24px;

        background-image: url('~@web/assets/images/menu/icon-menu-settings.svg');
      }

      &.-points {
        width: 24px;
        height: 24px;

        background-image: url("~@web/assets/images/menu/icon-menu-points.svg");
      }

      &.-link {
        width: 24px;
        height: 24px;
        background-image: url("~@web/assets/images/menu/icon-menu-linksocial.svg") !important;
      }

      &.-manual {
        width: 20px;
        height: 20px;

        background-image: url(~@web/assets/images/icon-manual-blue.svg);
        background-size: cover;
      }

      &.-calendar {
        width: 20px;
        height: 20px;

        background-image: url(~@web/assets/images/calendar.svg);
        background-size: cover;
      }

      &.-community {
        width: 24px;
        height: 24px;

        background-image: url(~@web/assets/images/menu/icon-menu-club.svg);
        background-size: cover;
      }
    }

    @media (min-width: $bp-desktop) {
      &.-mini &_icon {
        position: static;
        margin: auto;
        transform: none;
      }

      &_item:hover &_icon {
        position: absolute;
        display: block;
        transform: translateY(-50%);
      }
    }

    &_note {
      margin: 5px 0 5px 12px;
      padding: 3px 10px;

      color: rgb(255, 255, 255);
      font-size: 12px;
      line-height: 12px;
      white-space: pre;

      border-radius: 3px;
      background-image: linear-gradient(80deg, rgb(62, 98, 249) 0%, rgb(176, 63, 251) 100%);
    }

    @media (min-width: $bp-desktop) {
      &.-mini &_note {
        display: none;
      }

      &_item.-mini:hover &_note {
        display: inline-block;
      }
    }

    &_item.-gray &_note {
      background-color: rgb(200, 200, 200);
      background-image: none;
    }

    &.fade-enter-active,
    &.fade-leave-active {
      transition: opacity .5s;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }

    &_text {
      .asExternalDocument_link {
        margin-bottom: 0;
        color: #4c5264;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
      }
    }
  }

</style>
